import { useEffect, useState } from "react";
import AppIcon from "@/components/AppIcon";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, PencilLine, Share2, Users, ArrowRight, Check, TriangleAlert, Coins, EyeOff, Volume2, ArrowUp } from "lucide-react";
import api, { API, imageUrl } from "@/lib/api";
import Logo from "@/components/Logo";

const STEPS = [
  { Icon: PencilLine, title: "Tell Us Your Curiosity", body: "Search our growing knowledge community for the video and PDF guide you need. If it doesn't exist, join and let Vittyyo generate and create a complete video, PDF, and Keynote to solve your question—and help others learn too." },
  { Icon: Coins, title: "Make Passive Income!", body: "That's right! By creating your video, all items mentioned in it are automatically prepared and added to your video description for others to buy from Amazon. After receiving your Amazon Associate ID verification, you'll earn money for every purchase a user makes." },
  { Icon: Share2, title: "and there's more!", body: "You can also download your video, in landscape or portrait view and upload to any other platform you want, Youtube, Instagram and others. Spread your knowledge around the world and start a pretty cool side hustle at the same time. More surprises are in store, so join us, and start creating!." },
];

export default function WhatIsModal({ open, onClose }) {
  const navigate = useNavigate();
  const [demo, setDemo] = useState(null);

  useEffect(() => {
    if (open && demo === null) {
      api.get("/demo").then(({ data }) => setDemo(data)).catch(() => setDemo({ available: false }));
    }
  }, [open, demo]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto"
      style={{ background: "rgba(23,20,38,.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="whatis-modal"
    >
      <div className="flex min-h-full items-start md:items-center justify-center p-4">
      <div
        className="bg-[#4f7746] rounded-[26px] w-full max-w-2xl my-6 relative border-2 border-[#5d8a52]"
        style={{ boxShadow: "0 30px 80px -24px rgba(0,0,0,.7)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="whatis-close"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-white/70 hover:bg-black/25 hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-6 md:p-8">
          <style>{`
            @keyframes smsHintFade { 0%{opacity:0} 8%{opacity:1} 88%{opacity:1} 100%{opacity:0} }
            .sms-unmute-hint{ animation: smsHintFade 5s ease forwards; }
            @keyframes smsNudgeUp { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
            @keyframes smsNudgeDown { 0%,100%{transform:translateY(0)} 50%{transform:translateY(3px)} }
            .sms-unmute-up{ animation: smsNudgeUp 1s ease-in-out infinite; }
            .sms-unmute-down{ animation: smsNudgeDown 1s ease-in-out infinite; }
          `}</style>
          {/* Hero — brand logo + why you're here */}
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center justify-center gap-2 text-white font-bold text-base md:text-lg tracking-wide mb-2">
              <Sparkles size={19} /> WELCOME TO...
            </div>
            <Logo size="xl" />
            <h2 className="text-2xl md:text-3xl font-extrabold mt-2 leading-tight text-white">
              Watch This Video To Get Started!
            </h2>
          </div>

          {/* The video, front and center — shown in its TRUE proportions (portrait or landscape),
              never forced into a 16:9 box, so it can't look squished/cropped. */}
          {demo?.available && demo.has_video && (
            <div className="mt-4 flex justify-center">
              <div className="relative">
                <video
                  data-testid="whatis-demo-video"
                  src={`${API}/demo/video${demo.video_version ? `?v=${encodeURIComponent(demo.video_version)}` : ""}`}
                  controls
                  controlsList="nofullscreen"
                  playsInline
                  autoPlay
                  muted
                  poster={imageUrl(demo.hero_image_id) || undefined}
                  className="h-auto w-auto max-h-[70vh] max-w-full rounded-2xl border-2 border-[#5d8a52] bg-black object-contain"
                />
              </div>
            </div>
          )}

          {/* Persistent unmute nudge — small pill tucked to the bottom-right, in the gap below the video */}
          {demo?.available && demo.has_video && (
            <div className="mt-2 pr-1 flex justify-end items-center gap-1.5" data-testid="unmute-hint">
              <ArrowUp size={18} strokeWidth={3.5} className="text-white drop-shadow-[0_2px_3px_rgba(0,0,0,.85)]" />
              <div className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-extrabold text-white ring-2 ring-white/90 shadow-[0_3px_14px_rgba(0,0,0,.5)]">
                <Volume2 size={14} /> UnMute
              </div>
            </div>
          )}

          {/* What it is */}
          <div className="sms-tweed mt-5 rounded-2xl bg-[#0f0f16] border-2 border-[#5d8a52] p-4 md:p-5" data-testid="whatis-intro-card">
            <p className="text-white text-base md:text-lg leading-relaxed">
              Vittyyo transforms any topic into easy-to-follow learning resources. Instantly create a visual video, a
              complete PDF guide, or a Keynote presentation—all organized into clear, step-by-step instructions.
              Our current videos are presented as a sequence of high-quality still images with professional narration,
              making them fast to create and easy to follow. Full-motion video generation is already in development and
              will be available in a future update.
            </p>
          </div>

          {/* Honest capability disclaimer */}
          <div className="sms-tweed mt-4 rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] p-4" data-testid="whatis-limits">
            <div className="flex items-center justify-center gap-2 font-extrabold text-white text-lg">
              <TriangleAlert size={18} className="text-[#f5c518]" /> Important To Know <TriangleAlert size={18} className="text-[#f5c518]" />
            </div>
            <p className="text-white/85 text-base md:text-lg mt-1.5 leading-relaxed">
              Vittyyo is powered by AI, and like any AI it shines on some things more than others. It's brilliant with
              clear, hands-on, everyday tasks — and it can get fuzzy on highly technical or specialized jobs (think
              precision machinery or proprietary devices), where photos and steps may vary from reality. We're not saying
              don't try — just go in knowing the sweet spot, and always double-check anything that really matters.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div className="rounded-xl bg-black border-2 border-[#5d8a52] p-3">
                <div className="flex items-center gap-1.5 font-bold text-[#6ee7a8] text-sm">
                  <Check size={15} strokeWidth={3} /> Performs great
                </div>
                <ul className="mt-1.5 space-y-1 text-sm text-white/70">
                  <li>Recipes &amp; cooking</li>
                  <li>Gardening &amp; plants</li>
                  <li>Crafts, DIY &amp; home basics</li>
                  <li>Fitness, hobbies &amp; skills</li>
                  <li>Everyday how-tos ("wrap a gift")</li>
                </ul>
              </div>
              <div className="rounded-xl bg-black border-2 border-[#5d8a52] p-3">
                <div className="flex items-center gap-1.5 font-bold text-[#f5c518] text-sm">
                  <TriangleAlert size={15} /> More challenging
                </div>
                <ul className="mt-1.5 space-y-1 text-sm text-white/70">
                  <li>Rebuilding an electric motor</li>
                  <li>Servicing complex machinery</li>
                  <li>Proprietary / brand-specific devices</li>
                  <li>Highly technical or safety-critical work</li>
                  <li>Anything needing exact part diagrams</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-white/70 mt-3 leading-relaxed">
              The more specialized the task, the more results may vary — so use your judgment, and for anything technical
              or safety-related, verify with an expert or the manufacturer's instructions. <span className="font-semibold text-white">Use your judgment, and your balance wisely — we do not offer refunds on a result that doesn't work for you.</span>
            </p>
          </div>

          {/* How it works — 1, 2, 3 */}
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {STEPS.map(({ title, body }, i) => (
              <div key={i} className="sms-tweed rounded-2xl bg-[#0f0f16] border-2 border-[#5d8a52] p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-extrabold text-lg md:text-xl leading-tight pr-1 text-white">{i + 1}. {title}</div>
                  <span className="w-9 h-9 rounded-xl bg-[#4f7746] text-white flex items-center justify-center shrink-0">
                    <AppIcon size={22} />
                  </span>
                </div>
                <div className="text-base md:text-lg text-white/80 mt-2 leading-snug">{body}</div>
              </div>
            ))}
          </div>

          {/* CTAs — Browse (black) + Start (red) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            <button
              data-testid="whatis-community-btn"
              onClick={() => { onClose(); navigate("/"); }}
              className="inline-flex items-center justify-center gap-2 text-white font-extrabold py-3.5 px-4 rounded-2xl transition-transform hover:-translate-y-0.5 bg-[#0f0f16] border-2 border-[#5d8a52]"
            >
              <Users size={19} /> Browse Community
            </button>
            <button
              data-testid="whatis-start-btn"
              onClick={() => { onClose(); navigate("/studio"); }}
              className="inline-flex items-center justify-center gap-2 text-white font-extrabold py-3.5 px-4 rounded-2xl transition-colors bg-[#dc2626] hover:bg-[#b91c1c]"
            >
              <ArrowRight size={19} /> Start Creating
            </button>
          </div>

          {/* Don't show again */}
          <button
            data-testid="whatis-dont-show-btn"
            onClick={() => { try { localStorage.setItem("sms_hide_intro", "1"); } catch { /* noop */ } onClose(); }}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-2xl transition-colors bg-[#f5c518] hover:bg-[#e6b800] text-[#3a2f00]"
          >
            <EyeOff size={16} /> Don't show this again
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
